import React, { useState, useEffect } from 'react';
import { MessageSquare, Check, Shield, Bell } from 'lucide-react';
import { useSearchParams, Link } from 'react-router-dom';

const SmsOptIn = () => {
  const [searchParams] = useSearchParams();
  const businessParam = searchParams.get('business') || '';
  
  const [formData, setFormData] = useState({
    businessName: businessParam,
    phoneNumber: '',
    consent: false
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  // Update business name if URL parameter changes
  useEffect(() => {
    if (businessParam) {
      setFormData(prev => ({ ...prev, businessName: businessParam }));
    }
  }, [businessParam]);

  // Format phone number
  const formatPhoneNumber = (value) => {
    const phone = value.replace(/\D/g, '');
    if (phone.length <= 3) {
      return phone;
    } else if (phone.length <= 6) {
      return `(${phone.slice(0, 3)}) ${phone.slice(3)}`;
    } else {
      return `(${phone.slice(0, 3)}) ${phone.slice(3, 6)}-${phone.slice(6, 10)}`;
    }
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setFormData({ ...formData, phoneNumber: formatted });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    // Validate phone number
    const cleanPhone = formData.phoneNumber.replace(/\D/g, '');
    if (cleanPhone.length !== 10) {
      setError('Please enter a valid 10-digit phone number');
      return;
    }

    // Validate consent
    if (!formData.consent) {
      setError('Please agree to receive SMS messages');
      return;
    }

    // Success
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
      {/* Main Container */}
      <div className="relative w-full max-w-2xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg shadow-blue-500/20 mb-4">
            <MessageSquare className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-3">
            SMS Feedback <span className="text-blue-600">Opt-In</span>
          </h1>
          <p className="text-slate-600 text-lg">
            Get SMS updates and share your feedback
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-2xl p-8 md:p-12 border border-slate-200 shadow-xl">
          {submitted ? (
            // Success State
            <div className="text-center py-8">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-emerald-100 border border-emerald-200 mb-6">
                <Check className="w-10 h-10 text-emerald-600" />
              </div>
              <h2 className="text-3xl font-bold text-slate-900 mb-3">You're All Set!</h2>
              <p className="text-slate-600 text-lg mb-2">
                Thank you for opting in! You'll receive SMS messages from <span className="text-blue-600 font-semibold">{formData.businessName}</span> via our platform.
              </p>
              <p className="text-slate-500 text-sm">
                You'll start receiving feedback requests and updates shortly. Reply STOP at any time to unsubscribe.
              </p>
            </div>
          ) : (
            // Form
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Business Name */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Business Name
                </label>
                <input
                  type="text"
                  value={formData.businessName}
                  onChange={(e) => setFormData({ ...formData, businessName: e.target.value })}
                  placeholder="e.g., Davis Furniture Outlet"
                  required
                  readOnly={businessParam !== ''}
                  className={`w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none transition-colors ${
                    businessParam ? 'cursor-not-allowed opacity-75' : 'focus:border-blue-500 focus:ring-2 focus:ring-blue-200'
                  }`}
                />
                {businessParam && (
                  <p className="text-xs text-slate-500 mt-1">Business name provided by merchant</p>
                )}
              </div>

              {/* Phone Number */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Your Phone Number
                </label>
                <input
                  type="tel"
                  value={formData.phoneNumber}
                  onChange={handlePhoneChange}
                  placeholder="(555) 123-4567"
                  required
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors"
                />
              </div>

              {/* Consent Checkbox */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
                <label className="flex items-start space-x-3 cursor-pointer group">
                  <div className="relative flex-shrink-0 mt-1">
                    <input
                      type="checkbox"
                      checked={formData.consent}
                      onChange={(e) => setFormData({ ...formData, consent: e.target.checked })}
                      className="sr-only"
                      required
                    />
                    <div className={`w-5 h-5 rounded border-2 transition-all ${
                      formData.consent 
                        ? 'bg-blue-600 border-blue-600' 
                        : 'bg-white border-slate-300 group-hover:border-blue-500'
                    }`}>
                      {formData.consent && (
                        <Check className="w-full h-full text-white p-0.5" />
                      )}
                    </div>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-slate-700 leading-relaxed">
                      I consent to receive SMS text messages including customer satisfaction surveys, delivery updates, and service notifications from the business listed above. Messages are sent via EXITIUM's messaging platform. Message and data rates may apply. Message frequency varies. Reply <span className="text-blue-600 font-semibold">STOP</span> to unsubscribe at any time. Reply <span className="text-blue-600 font-semibold">HELP</span> for assistance.
                    </p>
                    <p className="text-xs text-slate-500 mt-2">
                      By checking this box, you agree to our{' '}
                      <Link to="/privacy-policy" className="text-blue-600 hover:text-blue-700 underline">
                        Privacy Policy
                      </Link>
                      {' '}and{' '}
                      <Link to="/terms" className="text-blue-600 hover:text-blue-700 underline">
                        Terms of Service
                      </Link>
                    </p>
                  </div>
                </label>
              </div>

              {/* Error Message */}
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                  {error}
                </div>
              )}

              {/* Info Cards */}
              <div className="grid md:grid-cols-3 gap-4">
                <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center mb-2">
                    <MessageSquare className="w-5 h-5 text-blue-600" />
                  </div>
                  <h3 className="text-slate-900 font-semibold text-sm mb-1">Quick Surveys</h3>
                  <p className="text-slate-500 text-xs">Share your feedback in seconds</p>
                </div>
                <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center mb-2">
                    <Bell className="w-5 h-5 text-purple-600" />
                  </div>
                  <h3 className="text-slate-900 font-semibold text-sm mb-1">Stay Updated</h3>
                  <p className="text-slate-500 text-xs">Get important notifications</p>
                </div>
                <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl">
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center mb-2">
                    <Shield className="w-5 h-5 text-emerald-600" />
                  </div>
                  <h3 className="text-slate-900 font-semibold text-sm mb-1">Your Privacy</h3>
                  <p className="text-slate-500 text-xs">Opt out anytime with STOP</p>
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                className="w-full py-4 rounded-xl font-semibold transition-all duration-300 bg-blue-600 hover:bg-blue-700 text-white text-lg shadow-lg shadow-blue-500/20 hover:shadow-xl hover:shadow-blue-500/30"
              >
                Confirm Opt-In
              </button>

              {/* Privacy Links */}
              <div className="text-center pt-4 border-t border-slate-200">
                <p className="text-xs text-slate-500 mb-2">
                  By submitting this form, you agree to our terms
                </p>
                <div className="flex items-center justify-center space-x-4 text-xs">
                  <Link to="/privacy-policy" className="text-blue-600 hover:text-blue-700 transition-colors">
                    Privacy Policy
                  </Link>
                  <span className="text-slate-400">•</span>
                  <Link to="/terms" className="text-blue-600 hover:text-blue-700 transition-colors">
                    Terms of Service
                  </Link>
                </div>
              </div>
            </form>
          )}
        </div>

        {/* Footer */}
        <div className="text-center mt-8">
          <a 
            href="https://exitiumlabs.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center space-x-2 text-slate-400 hover:text-slate-600 transition-colors"
          >
            <span className="text-sm">Powered by</span>
            <img src="/e.png" alt="Exitium" className="w-5 h-5 object-contain" />
            <span className="text-sm font-semibold">EXITIUM</span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default SmsOptIn;